import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Paper,
  Chip,
  CircularProgress,
  Divider,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import { QRCodeCanvas } from 'qrcode.react';
import { useTranslation } from 'react-i18next';
import firebaseDataService from '../../services/FirebaseDataService';
import { buildShiftQrPayload, QrDirection } from '../../utils/shiftQr';

interface VardiyaQrIndirModalProps {
  open: boolean;
  onClose: () => void;
  companyId?: string;
  /** Şube yöneticisi ise kendi şubesi (kilitli). */
  branchId?: string;
  isManager?: boolean;
}

interface BranchOpt {
  id: string;
  name: string;
}

const COMPANY_VALUE = '__company__';

const VardiyaQrIndirModal: React.FC<VardiyaQrIndirModalProps> = ({
  open,
  onClose,
  companyId,
  branchId,
  isManager = false,
}) => {
  const { i18n } = useTranslation();
  const isEnglish = i18n.language?.toLowerCase().startsWith('en');
  const tr = (trText: string, enText: string) => (isEnglish ? enText : trText);

  const [branches, setBranches] = useState<BranchOpt[]>([]);
  const [loadingBranches, setLoadingBranches] = useState(false);
  const [selected, setSelected] = useState<string>(COMPANY_VALUE);

  // Modal açıldığında şubeleri yükle + seçimi ilkle
  useEffect(() => {
    if (!open) return;

    // Şube yöneticisi ise kendi şubesine kilitle
    if (isManager && branchId) {
      setSelected(branchId);
    } else {
      setSelected(COMPANY_VALUE);
    }

    if (!companyId) return;
    setLoadingBranches(true);
    firebaseDataService
      .getBranches(companyId)
      .then((data) => {
        const arr = (data || []).map((b: any) => ({ id: b.id, name: b.name || tr('İsimsiz Şube', 'Unnamed Branch') }));
        setBranches(arr);
      })
      .catch((err) => {
        console.error('QR indir: şubeler yüklenemedi', err);
        setBranches([]);
      })
      .finally(() => setLoadingBranches(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, companyId, branchId, isManager]);

  // Seçili şube QR için (şirket geneli => null)
  const effectiveBranchId = selected === COMPANY_VALUE ? null : selected;

  // Seçim etiketi (dosya adı + başlık)
  const selectedLabel = useMemo(() => {
    if (selected === COMPANY_VALUE) return tr('Şirket Geneli', 'Company-wide');
    return branches.find((b) => b.id === selected)?.name || selected;
  }, [selected, branches, isEnglish]);

  // QR payload'ları
  const payloads = useMemo(() => {
    if (!companyId) return null;
    return {
      single: buildShiftQrPayload(companyId, { branchId: effectiveBranchId, direction: null }),
      in: buildShiftQrPayload(companyId, { branchId: effectiveBranchId, direction: 'in' }),
      out: buildShiftQrPayload(companyId, { branchId: effectiveBranchId, direction: 'out' }),
    };
  }, [companyId, effectiveBranchId]);

  const sanitize = (s: string) => s.replace(/[^\p{L}\p{N}_-]+/gu, '_').replace(/^_+|_+$/g, '') || 'QR';

  // Belirli yön canvas'ını, üzerine GİRİŞ/ÇIKIŞ + kapsam etiketi gömülü PNG olarak indir.
  // Etiket görsele işlendiği için basılan QR'lar birbirine karışmaz (yön gözle ayırt edilir).
  const downloadQr = (canvasId: string, direction: QrDirection) => {
    const src = document.getElementById(canvasId) as HTMLCanvasElement | null;
    if (!src) return;

    const dirLabel = direction === 'in' ? 'GİRİŞ' : direction === 'out' ? 'ÇIKIŞ' : 'TEK QR';
    const dirTag = direction === 'in' ? 'GIRIS' : direction === 'out' ? 'CIKIS' : 'TEK';
    const dirColor = direction === 'in' ? '#2e7d32' : direction === 'out' ? '#c62828' : '#455a64';

    const labelH = 140;
    const out = document.createElement('canvas');
    out.width = src.width;
    out.height = src.height + labelH;
    const ctx = out.getContext('2d');
    if (!ctx) return;

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, out.width, out.height);
    ctx.drawImage(src, 0, 0);

    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    // Yön (büyük, renkli)
    ctx.fillStyle = dirColor;
    ctx.font = 'bold 64px Arial, sans-serif';
    ctx.fillText(dirLabel, out.width / 2, src.height + 48);
    // Kapsam (şirket/şube)
    ctx.fillStyle = '#444444';
    ctx.font = '30px Arial, sans-serif';
    ctx.fillText(selectedLabel, out.width / 2, src.height + 100);

    const link = document.createElement('a');
    link.download = `QR_${sanitize(selectedLabel)}_${dirTag}.png`;
    link.href = out.toDataURL('image/png');
    link.click();
  };

  // Kart tanımları
  const cards: Array<{ key: string; canvasId: string; direction: QrDirection; title: string; color: 'success' | 'error' | 'default'; icon: React.ReactNode; value?: string }> = payloads
    ? [
        { key: 'in', canvasId: 'qr-indir-in', direction: 'in', title: tr('Giriş QR', 'Check-In QR'), color: 'success', icon: <LoginIcon fontSize="small" />, value: payloads.in },
        { key: 'out', canvasId: 'qr-indir-out', direction: 'out', title: tr('Çıkış QR', 'Check-Out QR'), color: 'error', icon: <LogoutIcon fontSize="small" />, value: payloads.out },
        { key: 'single', canvasId: 'qr-indir-single', direction: null, title: tr('Tek QR (otomatik)', 'Single QR (auto)'), color: 'default', icon: <QrCode2Icon fontSize="small" />, value: payloads.single },
      ]
    : [];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'primary.main', color: 'white', py: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <QrCode2Icon />
          <Typography variant="h6" fontWeight="bold">{tr('QR İndir', 'Download QR')}</Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {tr(
            'Şube seçin ve personelin okutacağı Giriş / Çıkış QR kodlarını indirin. Giriş QR’ını giriş kapısına, Çıkış QR’ını çıkış kapısına asın. Tek QR, otomatik (single) vardiyalar içindir.',
            'Select a branch and download the Check-In / Check-Out QR codes for personnel. Post the Check-In QR at the entrance and the Check-Out QR at the exit. The Single QR is for automatic (single) shifts.'
          )}
        </Typography>

        {/* Şube seçici */}
        <FormControl fullWidth size="small" sx={{ mb: 2 }} disabled={isManager || loadingBranches}>
          <InputLabel id="qr-branch-label">{tr('Şube', 'Branch')}</InputLabel>
          <Select
            labelId="qr-branch-label"
            label={tr('Şube', 'Branch')}
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
          >
            {!isManager && <MenuItem value={COMPANY_VALUE}>{tr('Şirket Geneli (tüm şubeler)', 'Company-wide (all branches)')}</MenuItem>}
            {branches.map((b) => (
              <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {loadingBranches && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}

        {!companyId ? (
          <Alert severity="warning">{tr('Şirket bilgisi bulunamadı.', 'Company information not found.')}</Alert>
        ) : (
          <>
            <Divider sx={{ mb: 2 }}>
              <Chip label={selectedLabel} size="small" color="primary" />
            </Divider>

            <Grid container spacing={2}>
              {cards.map((c) => (
                <Grid item xs={12} sm={4} key={c.key}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      textAlign: 'center',
                      borderRadius: 2,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 1.5,
                      borderColor: c.color === 'success' ? 'success.light' : c.color === 'error' ? 'error.light' : 'divider',
                    }}
                  >
                    <Chip icon={c.icon as any} label={c.title} size="small" color={c.color === 'default' ? undefined : c.color} variant={c.color === 'default' ? 'outlined' : 'filled'} />
                    <Box sx={{ bgcolor: '#fff', p: 1, borderRadius: 1, boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
                      {/* size=512 → yüksek çözünürlük; CSS ile 150px'e ölçekli gösterilir. İndirme 512px olur. */}
                      <QRCodeCanvas
                        id={c.canvasId}
                        value={c.value || ''}
                        size={512}
                        level="H"
                        includeMargin
                        bgColor="#ffffff"
                        fgColor="#000000"
                        style={{ width: 150, height: 150 }}
                      />
                    </Box>
                    <Button
                      variant="contained"
                      size="small"
                      color={c.color === 'default' ? 'primary' : c.color}
                      startIcon={<DownloadIcon />}
                      onClick={() => downloadQr(c.canvasId, c.direction)}
                      fullWidth
                    >
                      {tr('İndir', 'Download')}
                    </Button>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit">{tr('Kapat', 'Close')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default VardiyaQrIndirModal;
